import { orderBy, uniq } from "lodash";
import { memoizeOne } from "../../EasyProjectsCommon/memoizeOne";
import { getItemValue } from "../../Filters/helpers/getItemValue";
import { KeyOfString } from "../../typings/CommonTypes";
import { resComparer } from "../../utils/order/comparers";
import { ItemKey } from "../list";
import { OptionType } from "./MultiCheckbox";

type OptionTypeObject = KeyOfString<OptionType>;

const normalizeItems = memoizeOne((items: OptionType[]): OptionTypeObject => {
    const newItems: OptionTypeObject = {};

    for (const item of items) {
        if (!item.group) {
            newItems[getItemValue(item)] = item;
        }
    }

    return newItems;
});

const defaultSortFunc = (items: OptionType[]) => items.sort(resComparer);

export const orderByChecked = (items: OptionType[]) => {
    return orderBy(items, ["checked", item => item.id !== -1], ["desc", "asc"]);
};

export type SortOptions = F1<OptionType[], OptionType[]>;

export const takeItems = (
    allItems: OptionType[],
    count: number,
    recent: ItemKey[] = [],
    sortFunc: SortOptions = defaultSortFunc
) => {
    const result = allItems.filter(x => x.checked);
    if (result.length < count) {
        const reserveItems = sortFunc(allItems.filter(x => !x.checked));
        const reserve = orderBy(reserveItems, item => item.id !== -1)
            .slice(0, count)
            .map(x => getItemValue(x));
        const newRecent = uniq(recent.concat(reserve));

        const newHash = normalizeItems(allItems);
        for (const item of newRecent) {
            if (result.length >= count) {
                break;
            }

            const newItem = newHash[item];

            if (newItem && !newItem.checked) {
                result.push(newItem);
            }
        }
    }

    return sortFunc(result);
};

export const getItemsToShow = (
    prevItems: OptionType[],
    items: OptionType[],
    groupBy?: F1<OptionType[]>
): OptionType[] => {
    const recentHash = normalizeItems(prevItems);
    const newHash = normalizeItems(items);
    const result = [];

    // update value previous items
    for (const prev of prevItems) {
        const newItem = newHash[getItemValue(prev)];

        if (newItem) {
            result.push(newItem);
        }
    }

    // add new item to show
    for (const item of items) {
        const old = recentHash[getItemValue(item)];

        if (!old && item.checked) {
            result.push(item);
        }
    }

    if (groupBy) {
        return groupBy(result);
    }

    return result;
};
